import { useSyncExternalStore } from "react";
import { DEFAULT_FLOWS, ENTITY_JOB, ENTITY_PO, ENTITY_VISIT, OFF_FLOW, statusLabel } from "./checklistTransitions";

/**
 * What the app knows about every type's status flow, for the places that have a
 * STATUS but not its type's flow to hand: list pills and filters, dashboard
 * charts, badges, progress bars.
 *
 * Each job / visit / PO type owns its own flow (`statuses` on the type), and a
 * type can add statuses of its own. A global view spans many types, so it works
 * from the UNION: every status any type has, in the default order with the
 * custom ones placed after the built-in status they follow. `StatusRegistrySync`
 * (mounted once in the app shell) fills this from the type lists; it is a tiny
 * external store so a progress bar or filter rendered before the types arrive
 * re-renders when they do.
 *
 * ⚠️ Built-in statuses keep their DEFAULT label here. A type may rename one for
 * its own records (Settings → the type's Statuses) and the forms, the Show rule
 * and the checklist sections of that type show the rename; a list that spans
 * several types can't, since they may name it differently. A status a type
 * ADDED has one label (the first type that defines it) everywhere.
 */

const EMPTY = {
  version: 0,
  customLabels: {},
  flowsByType: { [ENTITY_JOB]: {}, [ENTITY_VISIT]: {}, [ENTITY_PO]: {} },
  catalog: {
    [ENTITY_JOB]: DEFAULT_FLOWS[ENTITY_JOB].map((f) => ({ ...f, builtin: true })),
    [ENTITY_VISIT]: DEFAULT_FLOWS[ENTITY_VISIT].map((f) => ({ ...f, builtin: true })),
    [ENTITY_PO]: DEFAULT_FLOWS[ENTITY_PO].map((f) => ({ ...f, builtin: true })),
  },
};

let state = EMPTY;
const listeners = new Set();

/** Index to insert a custom status at: just after the status it followed, never after the last one (that stays last). */
function insertionIndex(list, prevKey) {
  const at = prevKey == null ? -1 : list.findIndex((o) => o.key === prevKey);
  return at === -1 ? list.length - 1 : Math.min(at + 1, list.length - 1);
}

/** The union of every type's statuses for one entity: default order, custom ones after the status they follow. */
function buildCatalog(entity, types) {
  const out = DEFAULT_FLOWS[entity].map((f) => ({ ...f, builtin: true }));
  for (const type of types) {
    if (!type.has_custom_statuses) continue;
    const flow = type.statuses || [];
    flow.forEach((s, i) => {
      if (out.some((o) => o.key === s.key)) return;
      out.splice(insertionIndex(out, i === 0 ? null : flow[i - 1].key), 0, { key: s.key, label: s.label, builtin: false });
    });
  }
  return out;
}

/** Replace what the registry knows. Called with the three type lists. */
export function setStatusRegistry({ jobTypes = [], visitTypes = [], poTypes = [] }) {
  const byEntity = { [ENTITY_JOB]: jobTypes, [ENTITY_VISIT]: visitTypes, [ENTITY_PO]: poTypes };
  const customLabels = {};
  const flowsByType = { [ENTITY_JOB]: {}, [ENTITY_VISIT]: {}, [ENTITY_PO]: {} };
  const catalog = {};
  for (const [entity, types] of Object.entries(byEntity)) {
    catalog[entity] = buildCatalog(entity, types);
    for (const t of types) {
      const ref = entity === ENTITY_VISIT ? String(t.id) : t.name;
      flowsByType[entity][ref] = (t.statuses || []).map((s) => s.key);
      for (const s of t.statuses || []) {
        if (!DEFAULT_FLOWS[entity].some((f) => f.key === s.key) && !customLabels[s.key]) {
          customLabels[s.key] = s.label;
        }
      }
    }
  }
  state = { version: state.version + 1, customLabels, flowsByType, catalog };
  listeners.forEach((l) => l());
}

const subscribe = (l) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const getVersion = () => state.version;

/** Re-render when the registry changes. Components that read it call this once. */
export function useStatusRegistryVersion() {
  return useSyncExternalStore(subscribe, getVersion);
}

/** The statuses any type of this entity has, as [{ key, label, builtin }]. */
export function useStatusCatalog(entity) {
  useStatusRegistryVersion();
  return state.catalog[entity];
}

/** Non-hook read of the catalog — for code that already subscribed via `useStatusRegistryVersion`. */
export const catalogFor = (entity) => state.catalog[entity];

/** A dropdown's options for a list filter: every status any type has, then the fixed off-flow ones. */
export function filterStatusOptions(entity) {
  return [
    ...state.catalog[entity].map((c) => ({ value: c.key, label: c.label })),
    ...OFF_FLOW[entity].map((k) => ({ value: k, label: statusLabel(k) })),
  ];
}

/** A status's display label: a custom status's own name, else the default one. */
export function registryLabel(key) {
  return state.customLabels[key] ?? statusLabel(key);
}

/**
 * The status keys of the flow a record follows — its type's, when the type has a
 * list of its own — or null (use the default flow). `kind`: job | visit | po.
 */
export function flowKeysFor(kind, row) {
  const ref = kind === ENTITY_JOB ? row?.job_type : kind === ENTITY_VISIT ? row?.visit_type_id : row?.po_type;
  const keys = ref == null ? null : state.flowsByType[kind]?.[String(ref)];
  return keys && keys.length >= 2 ? keys : null;
}

/** Statuses that end a record's story — a list's "active" view excludes them. */
// ⚠️ Jobs: `on_hold` is NOT finished — it is open work a PM still tracks, so the
// dashboard's active lists keep showing a held job (it IS off-flow: no position,
// never moved automatically). Only `complete` and `cancelled` end a job's story.
export const FINISHED_STATUSES = {
  [ENTITY_JOB]: ["complete", ...OFF_FLOW[ENTITY_JOB].filter((k) => k !== "on_hold")],
  [ENTITY_VISIT]: ["complete", ...OFF_FLOW[ENTITY_VISIT]],
  [ENTITY_PO]: ["closed", ...OFF_FLOW[ENTITY_PO]],
};

/** The non-finished statuses of the catalog — what a list's pills call "active". */
export function activeStatusKeys(entity, catalog) {
  const finished = FINISHED_STATUSES[entity];
  // Off-flow statuses that are not finished (a held job) are open work too.
  const openOffFlow = OFF_FLOW[entity].filter((k) => !finished.includes(k));
  return [...catalog.map((c) => c.key), ...openOffFlow].filter((k) => !finished.includes(k));
}

/** Test hook: back to nothing known. */
export function resetStatusRegistry() {
  state = { ...EMPTY, version: state.version + 1 };
  listeners.forEach((l) => l());
}
